import type { HttpResponse } from '@angular/common/http';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, from, map, of, switchMap, tap, throwError } from 'rxjs';

import { AlertService } from './alert.service';
import { EncryptionService } from './encryption.service';
import { FileService } from './file-access.service';
import { environment } from '../../enviroment/enviroment';
import type { AlertResponse } from '../models/alert';
import type { Profile } from '../models/profile';
import { toCamelCase } from '../shared/utilities/case-conversion';
import { ViewsFacade } from '../store/views/views-facade..service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  http = inject(HttpClient);
  alertService = inject(AlertService);
  encryptionService = inject(EncryptionService);
  viewsFacade = inject(ViewsFacade);
  fileAccessService = inject(FileService);

  profile!: Profile;

  loadPermissions(): Observable<{
    res: HttpResponse<{ data: Profile }>;
    profilePicture: string;
  }> {
    const TOKEN = this.encryptionService.decodeBase64(localStorage.getItem('token'));
    const URL = environment.ANGULAR_APP_API_URL + 'onboarding/customer/get-user-details';

    const HEADERS = TOKEN ? new HttpHeaders({ authorization: TOKEN }) : new HttpHeaders();
    const OPTIONS = { headers: HEADERS, observe: 'response' as const };

    return this.http.post<{ data: Profile }>(URL, {}, OPTIONS).pipe(
      switchMap((res: HttpResponse<{ data: Profile }>) => {
        this.profile = toCamelCase(res.body?.data) as unknown as Profile;

        if (this.profile?.status === 'suspended') {
          const ALERT = this.alertService.error({
            heading: 'Account Suspended',
            message: 'Your account has been suspended. Please contact support.',
          });

          ALERT.then((result: AlertResponse) => {
            if (!result.key) {
              localStorage.clear();
              window.location.reload();
            }
          });
        }

        if (!this.profile?.profilePicture) {
          const PLACEHOLDER = environment.ANGULAR_APP_DEFAULT_PLACEHOLDER;
          return of({ res, profilePicture: PLACEHOLDER });
        }

        if (this.isFullUrl(this.profile.profilePicture)) {
          return of({
            res,
            profilePicture: this.profile.profilePicture,
          });
        }

        return from(this.fileAccessService.getSignedUrl$(this.profile.profilePicture)).pipe(
          catchError(() => of(environment.ANGULAR_APP_DEFAULT_PLACEHOLDER)),
          map((signedUrl) => ({ res, profilePicture: signedUrl }))
        );
      }),

      tap(({ profilePicture }) => {
        this.viewsFacade.setProfileImage(profilePicture);
        this.viewsFacade.setProfile(this.profile);
      }),

      catchError((e) => {
        if (e?.status === 401) {
          localStorage.clear();
          window.location.reload();
        }
        return throwError(() => e);
      })
    );
  }

  private isFullUrl(url: string): boolean {
    return /^https?:\/\//i.test(url);
  }
}
